import React, { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import type { FaixaDePreco, TipoArredondamento, TipoFinais } from '../../../../../@types/IPA/PrecoPsicologico';
import { FinaisPermitidos } from '../FinaisPermitidos';
import { OpcoesArredondamento } from '../OpcoesArredondamento';
import { SimuladorDePreco } from '../SimuladorDePreco';
import { TipoSeletorDigitos } from '../TipoSeletorDigitos';
import { faixaDePrecoSchema, type FaixaDePrecoFormData } from './schema';

interface Props {
  faixaEmEdicao?: FaixaDePreco | null;
  onSave: (data: FaixaDePrecoFormData) => void;
  onCancel: () => void;
  onDelete?: () => void;
  erroSobreposicao?: boolean;
  salvando?: boolean;
}

const VALORES_PADRAO: FaixaDePrecoFormData = {
  valorDe: 0,
  valorAte: 0,
  tipo: 1,
  opcaoArredondamento: 'MAIS_PROXIMO',
  finaisPermitidos: [''],
  usuarioId: '',
};

function paraDadosForm(faixa?: FaixaDePreco | null): FaixaDePrecoFormData {
  if (!faixa) return VALORES_PADRAO;
  return {
    valorDe: faixa.valorDe,
    valorAte: faixa.valorAte,
    tipo: faixa.tipo,
    opcaoArredondamento: faixa.opcaoArredondamento,
    finaisPermitidos: faixa.finaisPermitidos.length > 0 ? faixa.finaisPermitidos : [''],
    usuarioId: '',
  };
}

function formatarMoeda(valor: number): string {
  const raw = Math.round(valor * 100);
  return (raw / 100).toFixed(2).replace('.', ',');
}

function parseMoeda(texto: string): number {
  const raw = texto.replace(/\D/g, '');
  return (parseInt(raw, 10) || 0) / 100;
}

function InputMoeda({ value, onChange, onBlur, hasError }: { value: number; onChange: (v: number) => void; onBlur?: () => void; hasError?: boolean }) {
  const mascara = value > 0
    ? value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '';

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    onChange(parseMoeda(e.target.value));
  }

  return (
    <div className={`prf__input-group${hasError ? ' prf__input-group--erro' : ''}`}>
      <span className="prf__input-addon">R$</span>
      <input
        type="text"
        className="prf__input"
        value={mascara}
        onChange={handleChange}
        onBlur={onBlur}
        placeholder="00,00"
      />
    </div>
  );
}

export function FaixaDePrecoForm({ faixaEmEdicao, onSave, onCancel, onDelete, erroSobreposicao, salvando }: Props) {
  const usuarioId = JSON.parse(localStorage.getItem('usuario') || '{}')?.email ?? '';

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<FaixaDePrecoFormData>({
    resolver: zodResolver(faixaDePrecoSchema),
    defaultValues: { ...paraDadosForm(faixaEmEdicao), usuarioId },
    mode: 'onSubmit',
  });

  useEffect(() => {
    reset({ ...paraDadosForm(faixaEmEdicao), usuarioId });
  }, [faixaEmEdicao, reset, usuarioId]);

  const tipoAtual = watch('tipo') as TipoFinais;
  const finaisAtuais = watch('finaisPermitidos');
  const opcaoAtual = watch('opcaoArredondamento') as TipoArredondamento;
  const valorDeAtual = watch('valorDe');
  const valorAteAtual = watch('valorAte');

  function onSubmit(data: FaixaDePrecoFormData) {
    onSave({ ...data, usuarioId });
  }

  return (
    <form
      className="prf"
      onSubmit={handleSubmit(onSubmit)}
      onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}
    >
      {/* Faixa de preço */}
      <div className="prf__card">
        <div className="prf__card-header">
          <span className="prf__card-titulo">Faixa de preço</span>
          <span className="prf__card-subtitulo">Defina o intervalo de preço para essa regra</span>
        </div>

        <div className="prf__faixa-campos">
          <div className="prf__campo">
            <div className="prf__campo-label">De</div>
            <Controller
              name="valorDe"
              control={control}
              render={({ field }) => (
                <InputMoeda
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  hasError={!!errors.valorDe || erroSobreposicao}
                />
              )}
            />
            {errors.valorDe && <span className="prf__erro">{errors.valorDe.message}</span>}
          </div>
          <div className="prf__campo">
            <div className="prf__campo-label">Até</div>
            <Controller
              name="valorAte"
              control={control}
              render={({ field }) => (
                <InputMoeda
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  hasError={!!errors.valorAte || erroSobreposicao}
                />
              )}
            />
            {errors.valorAte && <span className="prf__erro">{errors.valorAte.message}</span>}
          </div>
        </div>
      </div>

      {/* Dígitos arredondados */}
      <div className="prf__card">
        <Controller
          name="tipo"
          control={control}
          render={({ field }) => (
            <TipoSeletorDigitos
              value={field.value}
              valorAte={valorAteAtual}
              onChange={v => {
                field.onChange(v);
              }}
            />
          )}
        />
        {errors.tipo && <span className="prf__erro">{errors.tipo.message}</span>}
      </div>

      {/* Final + Arredondamento */}
      <div className="prf__card">
        <Controller
          name="finaisPermitidos"
          control={control}
          render={({ field }) => (
            <FinaisPermitidos
              tipo={tipoAtual}
              value={field.value}
              onChange={field.onChange}
              error={errors.finaisPermitidos?.message}
            />
          )}
        />

        <Controller
          name="opcaoArredondamento"
          control={control}
          render={({ field }) => (
            <OpcoesArredondamento
              value={field.value as TipoArredondamento}
              onChange={field.onChange}
              error={errors.opcaoArredondamento?.message}
            />
          )}
        />
      </div>

      {/* Simulador */}
      <div className="prf__card">
        <SimuladorDePreco
          tipo={tipoAtual}
          finaisPermitidos={finaisAtuais?.filter(Boolean) ?? []}
          opcaoArredondamento={opcaoAtual}
          valorDe={valorDeAtual}
          valorAte={valorAteAtual}
        />
      </div>

      {/* Botões */}
      <div className="prf__card">
        <div className="prf__acoes">
          {faixaEmEdicao?.id && onDelete && (
            <button
              type="button"
              className="prf__btn prf__btn--excluir"
              onClick={onDelete}
              disabled={salvando}
            >
              EXCLUIR
            </button>
          )}
          <div className="prf__acoes-direita">
            <button
              type="button"
              className="prf__btn prf__btn--cancelar"
              onClick={onCancel}
              disabled={salvando}
            >
              CANCELAR
            </button>
            <button
              type="submit"
              className="prf__btn prf__btn--salvar"
              disabled={salvando}
            >
              {salvando ? 'SALVANDO...' : 'SALVAR'}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
}
